import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { Contact, ContactLogic } from '@/modules/contact';
import type { ContactList } from '@/modules/contact';
import type { ContactAction, ContactIndex } from '@/modules/contact/reducer';
import { selectAllContacts } from '@/modules/contact/reducer';
import type { Topic } from '@/modules/topic/types';
import { MePrFoService } from '@/services/meprfo';

export interface UseUpdateRecipientMatchParams {
  /** Only the update flow prepopulates; every other flow starts empty. */
  isUpdateFlow: boolean;
  topic: Topic | null;
  /** True until both the stored session and the topic have loaded. */
  isLoadingLegState: boolean;
  inProgressLegId: string | null;
  /** The head of the change list being shared, which scopes the candidates. */
  aboutChangesSince: string | undefined;
  /** The whole address book, read through a ref — see the effect's note. */
  state: ContactIndex;
  /** The live recipient selection, also read through a ref. */
  recipients: ContactList;
  dispatch: React.Dispatch<ContactAction>;
  /**
   * Cache PSI-resolved server ids back onto their contacts, from
   * `useTopicAwareness` — the same write-back the awareness check does.
   */
  enrichResolvedIds: (
    contacts: readonly Contact[],
    resolvedIds: ReadonlyMap<string, string>
  ) => void;
  /**
   * Collapse the recipient box. A match can add a long list of past
   * recipients, so the update flow opens with them folded behind "+n".
   */
  collapseRecipientBox: () => void;
}

export interface UpdateRecipientMatch {
  /**
   * Non-null while the match hashes the address book; surfaced to
   * RecipientsStep as a progress indicator (#427).
   */
  recipientMatchProgress: { done: number; total: number } | null;
}

/**
 * Start an update by pre-adding the people this sender last messaged about
 * this topic (#427), found by intersecting the address book with the server's
 * candidates over a labeled OPRF-PSI round-trip.
 *
 * One shot per wizard mount, and only into an empty box: a sender who has
 * already picked recipients, or come back to a send in flight, keeps what
 * they have.
 */
export function useUpdateRecipientMatch({
  isUpdateFlow,
  topic,
  isLoadingLegState,
  inProgressLegId,
  aboutChangesSince,
  state,
  recipients,
  dispatch,
  enrichResolvedIds,
  collapseRecipientBox,
}: UseUpdateRecipientMatchParams): UpdateRecipientMatch {
  const [recipientMatchProgress, setRecipientMatchProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const stateRef = useRef(state);
  stateRef.current = state;
  const recipientsLengthRef = useRef(recipients.length);
  recipientsLengthRef.current = recipients.length;

  // Update-flow recipient prepopulation via labeled OPRF-PSI (#427): intersect
  // the whole address book with the recipients the current user previously
  // messaged about this topic (one candidates fetch + one batched OPRF
  // round-trip), keep those last messaged before the change-list head, and
  // pre-add them as recipients. Best-effort — a failure just leaves the
  // recipient box empty. Runs once per wizard mount, and only when the user
  // hasn't already started (no recipients, no in-progress leg).
  const hasPrepopulatedRecipients = useRef(false);
  useEffect(() => {
    if (!isUpdateFlow || !topic || isLoadingLegState) return;
    if (hasPrepopulatedRecipients.current) return;
    if (recipientsLengthRef.current > 0 || inProgressLegId) {
      // The user (or a restored session) already has recipients in play;
      // don't fight them. Latch so a later removal doesn't re-trigger.
      hasPrepopulatedRecipients.current = true;
      return;
    }
    hasPrepopulatedRecipients.current = true;
    // Start the update flow with a collapsed recipient box: prepopulation can
    // add a long list of past recipients and we don't want to overwhelm the
    // user with all of them up front. They can tap "+n" to expand.
    collapseRecipientBox();

    let cancelled = false;
    // Aborts the in-flight OPRF hashing when the effect tears down (e.g. the
    // user closes the wizard), so we stop grinding through the address book
    // instead of finishing invisibly in the background.
    const abortController = new AbortController();
    (async () => {
      try {
        const allContacts = selectAllContacts(stateRef.current).filter(
          ContactLogic.isReachable
        );
        if (allContacts.length === 0) return;
        // The service echoes back the same ContactLike references it was
        // given, so an identity map recovers each match's source Contact.
        const likeToContact = new Map(
          allContacts.map(c => [ContactLogic.toContactLike(c), c] as const)
        );
        // The backend scopes candidates to those last messaged before the
        // change-list head, so the client hashes against a smaller set.
        const { matches, resolvedIds } =
          await MePrFoService.matchMyPastRecipients(
            topic.id,
            Array.from(likeToContact.keys()),
            aboutChangesSince,
            {
              signal: abortController.signal,
              onProgress: (done, total) => {
                // Drive the RecipientsStep bar. A tiny address book finishes in
                // one chunk, so the bar just flashes — harmless.
                if (cancelled) return;
                setRecipientMatchProgress(total > 0 ? { done, total } : null);
              },
            }
          );
        if (cancelled || matches.length === 0) return;

        // Cache freshly PSI-resolved server IDs back onto contacts, mirroring
        // the awareness effect above, so future lookups skip the OPRF.
        enrichResolvedIds(allContacts, resolvedIds);

        // Every match already predates the change-list head (filtered
        // server-side), so prepopulate them all.
        const toAdd = matches
          .map(m => likeToContact.get(m.contact))
          .filter((c): c is Contact => c !== undefined);
        if (toAdd.length > 0 && !cancelled) {
          dispatch({ type: 'ADD_RECIPIENTS', payload: toAdd });
        }
      } catch {
        // Best-effort: never block the wizard on prepopulation.
      } finally {
        // Clear the indicator on success, failure, or abort.
        if (!cancelled) setRecipientMatchProgress(null);
      }
    })();
    return () => {
      cancelled = true;
      abortController.abort();
      setRecipientMatchProgress(null);
    };
    // `state` and `recipients.length` are intentionally read via refs, not
    // listed here: the effect is a one-shot latch, and depending on them would
    // let unrelated address-book churn abort the in-flight OPRF hashing (#436).
  }, [
    isUpdateFlow,
    topic,
    isLoadingLegState,
    inProgressLegId,
    aboutChangesSince,
    dispatch,
    enrichResolvedIds,
    collapseRecipientBox,
  ]);

  return { recipientMatchProgress };
}
